/**
 * Dettaglio di una scheda di libreria (V65): il mix raggruppato per famiglia,
 * con la quota di ogni attributo, e le varianti se è una base; una variante
 * porta alla sua base. Modifica e nuova variante si aprono in un modale.
 * Admin legge senza i pulsanti di scrittura.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ETICHETTA_FAMIGLIA_ATTRIBUTO,
  ETICHETTA_TIPO_SCHEDA,
  FAMIGLIE_ATTRIBUTO,
  puo,
} from '@calcio/shared';
import {
  ChartPieIcon,
  GitBranchIcon,
  PencilIcon,
  PowerIcon,
  PowerOffIcon,
  Trash2Icon,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { chiama } from '../api/client.js';
import { AzioniTestata, MenuAzioni, PulsantePrincipale } from '../comuni/Azioni.js';
import { Corpo } from '../comuni/Corpo.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { IconaFamiglia } from '../comuni/famiglie.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { useContesto } from '../navigazione/contesto.js';
import { DialogScheda } from './DialogScheda.js';
import { ICONA_TIPO_SCHEDA } from './icone.js';
import { DatiScheda, RigaScheda } from './RigaScheda.js';
import type { SchedaDettaglio } from './schede.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

export function DettaglioScheda() {
  const { ss, id } = useParams<{ ss: string; id: string }>();
  const navigate = useNavigate();
  const { utente } = useContesto();
  const { dati, errore, ricarica } = useDati<SchedaDettaglio>(id ? `/schede/${id}` : '');
  const [eliminaAperta, setEliminaAperta] = useState(false);
  const [modale, setModale] = useState<'modifica' | 'variante' | null>(null);
  const gestisce = Boolean(ss && puo(utente.assegnazioni, 'allenamento.gestisci', ss));

  if (!dati) {
    return (
      <>
        <Testata titolo="Scheda" indietro={`/sq/${ss}/schede`} />
        <Corpo>
          <StatoCaricamento errore={errore} />
        </Corpo>
      </>
    );
  }

  const base = !dati.schedaBaseId;
  const cambiaStato = async () => {
    try {
      await chiama(`/schede/${dati.id}/stato`, {
        metodo: 'PUT',
        corpo: { attiva: !dati.attiva },
      });
      await ricarica();
      toast.success(dati.attiva ? 'Scheda disattivata.' : 'Scheda riattivata.');
    } catch (e) {
      toast.error(messaggio(e));
    }
  };

  const elimina = async () => {
    try {
      await chiama(`/schede/${dati.id}`, { metodo: 'DELETE' });
      toast.success(`${dati.nome} eliminata.`);
      navigate(`/sq/${ss}/schede`, { replace: true });
    } catch (e) {
      toast.error(messaggio(e));
      setEliminaAperta(false);
    }
  };

  const Tipo = ICONA_TIPO_SCHEDA[dati.tipo];
  const perFamiglia = FAMIGLIE_ATTRIBUTO.map((famiglia) => {
    const voci = dati.mix.filter((v) => v.famiglia === famiglia);
    return { famiglia, voci, totale: voci.reduce((t, v) => t + v.quota, 0) };
  }).filter((f) => f.voci.length > 0);
  const conVarianti = base && dati.varianti.length > 0;
  const azioni = gestisce && (
    <AzioniTestata>
      <PulsantePrincipale
        etichetta="Modifica"
        Icona={PencilIcon}
        onClick={() => setModale('modifica')}
      />
      <MenuAzioni
        voci={[
          base && {
            etichetta: 'Nuova variante',
            Icona: GitBranchIcon,
            onSelect: () => setModale('variante'),
          },
          {
            etichetta: dati.attiva ? 'Disattiva' : 'Riattiva',
            Icona: dati.attiva ? PowerOffIcon : PowerIcon,
            onSelect: () => void cambiaStato(),
          },
          {
            etichetta: 'Elimina',
            Icona: Trash2Icon,
            onSelect: () => setEliminaAperta(true),
            disabled: conVarianti,
            motivo: 'Ha varianti: elimina prima quelle.',
            distruttiva: true,
          },
        ]}
      />
    </AzioniTestata>
  );

  return (
    <>
      <Testata
        titolo={dati.nome}
        sopra={
          dati.schedaBaseId && (
            <span className="flex items-center gap-1">
              <GitBranchIcon aria-hidden className="size-3.5" />
              Variante di{' '}
              <Link
                to={`/sq/${ss}/schede/${dati.schedaBaseId}`}
                className="font-medium text-foreground underline-offset-4 hover:underline"
              >
                {dati.nomeBase}
              </Link>
            </span>
          )
        }
        indietro={`/sq/${ss}/schede`}
        azione={azioni}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="flex items-center gap-1 font-medium">
            <Tipo aria-hidden className="size-4 text-muted-foreground" />
            {ETICHETTA_TIPO_SCHEDA[dati.tipo]}
          </span>
          <DatiScheda rpe={dati.rpeRiferimento} minuti={dati.minutiRiferimento} />
          {!dati.attiva && <Badge variant="outline">Disattivata</Badge>}
        </div>
      </Testata>
      <Corpo className="flex flex-col gap-4">
        <div className="grid items-start gap-4 @5xl:grid-cols-2">
          <Card className="bg-muted">
            <CardHeader>
              <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
                <ChartPieIcon aria-hidden className="size-5" />
                <h2>Mix</h2>
                <Badge className="tabular-nums">{dati.mix.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 px-2 @xl:px-(--card-spacing)">
              {perFamiglia.map(({ famiglia, voci, totale }) => (
                <section
                  key={famiglia}
                  className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3"
                >
                  <h3 className="flex items-center gap-2 font-semibold">
                    <IconaFamiglia famiglia={famiglia} className="size-5" />
                    <span className="flex-1">{ETICHETTA_FAMIGLIA_ATTRIBUTO[famiglia]}</span>
                    <span className="tabular-nums">{totale}%</span>
                  </h3>
                  <ul className="flex flex-col gap-1.5">
                    {voci.map((v) => (
                      <li key={v.attributoId} className="flex items-center gap-3 text-sm">
                        <span className="min-w-0 flex-1 truncate">{v.nome}</span>
                        <Progress
                          value={v.quota}
                          aria-label={`Quota di ${v.nome}`}
                          className="w-24 @md:w-40"
                        />
                        <span className="w-8 text-right font-medium tabular-nums">{v.quota}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </CardContent>
          </Card>

          {base && (
            <Card className="bg-muted">
              <CardHeader>
                <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
                  <GitBranchIcon aria-hidden className="size-5" />
                  <h2>Varianti</h2>
                  <Badge className="tabular-nums">{dati.varianti.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-2 @xl:px-(--card-spacing)">
                {dati.varianti.length === 0 ? (
                  <StatoVuoto
                    titolo="Nessuna variante"
                    descrizione="Si crea da questa scheda, con lo stesso mix da ritoccare."
                  />
                ) : (
                  <ul className="flex flex-col gap-2">
                    {dati.varianti.map((v) => (
                      <li key={v.id}>
                        <RigaScheda scheda={v} ss={ss!} />
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </Corpo>

      {gestisce && (
        <DialogScheda
          ss={ss!}
          aperto={modale !== null}
          onChiudi={() => setModale(null)}
          esistente={modale === 'modifica' ? dati : undefined}
          base={modale === 'variante' ? dati : undefined}
          onSalvato={ricarica}
        />
      )}

      <AlertDialog open={eliminaAperta} onOpenChange={setEliminaAperta}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {dati.nome}?</AlertDialogTitle>
            <AlertDialogDescription>
              La scheda esce dalla libreria. Il mix non si recupera.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => void elimina()}>
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
